import * as rrweb from "rrweb";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { humanId, adjectives, nouns, verbs, adverbs } from "human-id";
import * as FingerprintJS from "@fingerprintjs/fingerprintjs";
import { SessionableOptions, SessionMetrics, SessionPayload } from "../types";
import { listenerHandler } from "@rrweb/types";

/**
 * Core Sessionable class for session recording
 */
export class Sessionable {
  private options: SessionableOptions & {
    apiKey: string;
    autoStart: boolean;
    metadata: Record<string, any>;
    maskInputs: boolean;
    maskTextSelector?: string;
    debug: boolean;
    useDeviceId: boolean;
    recordLocalhost: boolean;
    minSessionDuration: number;
    inlineImages: boolean;
  };
  private anonymousUserId: string | null = null;
  private userId: string | null = null;
  private organizationId: string | null = null;
  private userAttributes: Record<string, any> | null = null;
  private events: rrweb.eventWithTime[] = [];
  private pendingEvents: rrweb.eventWithTime[] = []; // Events waiting for ID generation
  private stopFn: listenerHandler | null = null;
  private eventsInterval: ReturnType<typeof setInterval> | null = null;
  private metrics: SessionMetrics;
  private idGenerationInProgress: boolean = false;
  // User identification tracking
  private lastIdentifiedUserId: string | null = null;
  private lastIdentifiedOrgId: string | null = null;
  private identifyInProgress: boolean = false;
  private pendingIdentify: (() => Promise<void>) | null = null;
  // Session duration tracking
  private isNewSession: boolean = true;
  private sessionStartTime: number = 0;
  // Snapshot tracking
  private initialFullSnapshot: rrweb.eventWithTime | null = null;
  private snapshotTimeoutId: ReturnType<typeof setTimeout> | null = null;
  // API endpoints
  private productionEndpoint = "https://sessionable.pages.dev/api/sessions";
  private debugEndpoint = "http://localhost:5173/api/sessions";
  private batchSize = 50;
  private batchInterval = 5000; // ms
  // Session expiration time (30 minutes in milliseconds)
  private static readonly SESSION_EXPIRATION_TIME = 30 * 60 * 1000;
  // Local storage keys
  private static readonly ANON_ID_STORAGE_KEY = "sessionable_anonymous_id";
  private static readonly SESSION_ID_STORAGE_KEY = "sessionable_session_id";
  private static readonly SESSION_TIMESTAMP_KEY = "sessionable_last_activity";

  /**
   * Creates a new Sessionable instance
   */
  constructor(options: SessionableOptions) {
    // Set default options
    this.options = {
      apiKey: options.apiKey,
      autoStart: options.autoStart || false,
      metadata: options.metadata || {},
      maskInputs: options.maskInputs || false,
      maskTextSelector: options.maskTextSelector,
      debug: options.debug || false,
      useDeviceId:
        options.useDeviceId !== undefined ? options.useDeviceId : true,
      recordLocalhost:
        options.recordLocalhost !== undefined ? options.recordLocalhost : false,
      minSessionDuration:
        options.minSessionDuration !== undefined
          ? options.minSessionDuration
          : 5000,
      inlineImages: 
        options.inlineImages !== undefined ? options.inlineImages : true,
    };

    // Initialize or retrieve the anonymous user ID
    this.initializeAnonymousId();

    // Initialize metrics
    this.metrics = {
      sessionId: "",
      eventsRecorded: 0,
      batchesSent: 0,
      batchesSuccessful: 0,
      connectionStatus: "disconnected",
      lastError: null,
      lastBatchSize: 0,
      lastSuccessfulUpload: null,
      recordingStartTime: new Date(),
      isRecording: false,
    };

    // Set user identification if provided during initialization
    if (options.userId) {
      this.userId = options.userId;
      this.organizationId = options.organizationId || null;
      this.userAttributes = options.userAttributes || {};

      if (this.options.debug) {
        console.log(
          `[Sessionable] User identified during initialization: ${options.userId}`
        );
      }
    }

    // Automatically start recording if option is set
    if (this.options.autoStart) {
      this.start();
    }
  }

  /**
   * Check if the current environment is a development environment
   * @returns True if the current environment is a dev environment (localhost, etc.)
   */
  private isDevEnvironment(): boolean {
    if (typeof window === "undefined" || !window.location) return false;

    const hostname = window.location.hostname.toLowerCase();
    // Common development hostnames
    const devDomains = ["localhost", "127.0.0.1"];

    // Check if hostname matches any of the dev domains
    return devDomains.some((domain) => {
      if (domain.startsWith(".")) {
        // For domain suffixes like .local, .test, etc.
        return hostname.endsWith(domain);
      }
      return hostname === domain;
    });
  }

  /**
   * Start recording the session
   */
  public start(): void {
    if (this.metrics.isRecording) return;
    
    // Reset snapshot tracking
    this.initialFullSnapshot = null;
    
    // Clear any existing timeout
    if (this.snapshotTimeoutId) {
      clearTimeout(this.snapshotTimeoutId);
      this.snapshotTimeoutId = null;
    }

    // Check if we should skip recording in dev environments
    if (!this.options.recordLocalhost && this.isDevEnvironment()) {
      if (this.options.debug) {
        console.log(
          `[Sessionable] Recording disabled on '${window.location.hostname}' (development environment). ` +
            `Set recordLocalhost: true to enable recording in development.`
        );
      }

      // Update metrics to show "disabled" state
      this.updateMetrics({
        connectionStatus: "disabled",
        lastError: "Recording disabled in development environment",
        isRecording: false,
      });

      return;
    }

    // Get the session ID and resumption status
    const { sessionId, isResuming } = this.getOrCreateSessionId();

    // Reset events array
    this.events = [];

    // If resuming an existing session, add a resumption event
    if (isResuming) {
      this.addResumptionEvent();
    }

    // Update metrics
    this.updateMetrics({
      sessionId,
      recordingStartTime: new Date(),
      eventsRecorded: 0,
      batchesSent: 0,
      batchesSuccessful: 0,
      connectionStatus: "connecting",
      isRecording: true,
    });
    
    if (this.options.debug) {
      console.log("[Sessionable] Starting recording session");
    }
    
    // Set a timeout to handle cases where rrweb doesn't emit a snapshot
    this.snapshotTimeoutId = setTimeout(() => {
      if (this.initialFullSnapshot === null && this.metrics.isRecording) {
        console.warn("[Sessionable] No full snapshot received after timeout, attempting to generate one");
        this.generateFullSnapshot();
      }
    }, 5000); // 5 second timeout

    // Start recording with rrweb
    const stop = rrweb.record({
      emit: (event) => {
        // Capture the initial full snapshot
        if (event.type === 2 && this.initialFullSnapshot === null) {
          this.initialFullSnapshot = event;
          
          if (this.options.debug) {
            console.log("[Sessionable] Captured initial full snapshot");
          }
        }
        
        // Snapshot detection log (essential for debugging)
        if (event.type === 2) {
          console.log('[Sessionable DEBUG] ✅ FULL SNAPSHOT RECEIVED');
        }
        
        // Update metrics
        this.updateMetrics({
          eventsRecorded: this.metrics.eventsRecorded + 1,
        });

        // If ID is still generating, add to pending queue
        if (!this.anonymousUserId || this.idGenerationInProgress) {
          this.pendingEvents.push(event);
          if (this.options.debug && this.pendingEvents.length === 1) {
            console.log(
              "[Sessionable] Queueing events until ID generation completes"
            );
          }
          return;
        }

        // Push event to array
        this.events.push(event);

        // Send batch if we've reached the threshold
        if (this.events.length >= this.batchSize) {
          const eventsToSend = [...this.events];
          this.sendEvents(eventsToSend);
          this.events = [];
        }
      },
      // Configure rrweb options based on user settings
      inlineStylesheet: true,
      maskAllInputs: this.options.maskInputs,
      maskTextSelector: this.options.maskTextSelector,
      recordCanvas: true,
      collectFonts: true,
      inlineImages: this.options.inlineImages,
    });

    // Set up interval to periodically send events
    this.eventsInterval = setInterval(() => {
      // Only process events if we have an ID
      if (this.anonymousUserId && !this.idGenerationInProgress) {
        // Process any pending events first
        this.processPendingEvents();

        // Then process regular events
        if (this.events.length > 0) {
          const eventsToSend = [...this.events];
          this.sendEvents(eventsToSend);
          this.events = [];
        }
      }
    }, this.batchInterval);

    // Save stop function for later
    this.stopFn = stop || null;
  }

  /**
   * Stop recording the session
   * @param clearSession - Whether to clear session data from storage (default: false)
   */
  public stop(clearSession: boolean = false): void {
    if (!this.metrics.isRecording || !this.stopFn) return;

    // Clear the snapshot timeout
    if (this.snapshotTimeoutId) {
      clearTimeout(this.snapshotTimeoutId);
      this.snapshotTimeoutId = null;
    }
    
    // Before stopping, check if we have a full snapshot in our events (essential for debugging)
    const hasFullSnapshotInRegular = this.events.some(event => event.type === 2);
    const hasFullSnapshotInPending = this.pendingEvents.some(event => event.type === 2);
    
    if (hasFullSnapshotInRegular || hasFullSnapshotInPending) {
      console.log(`[Sessionable DEBUG] ✅ Full snapshot detected before stop`);
    } else {
      console.log(`[Sessionable DEBUG] ⚠️ No full snapshot found in events before stop`);
      
      // CRITICAL FIX: Request a full snapshot before stopping
      if (this.metrics.batchesSent === 0) {
        try {
          // Add the initial snapshot if we have one but it's not in our events
          if (this.initialFullSnapshot) {
            console.log(`[Sessionable DEBUG] ✅ Using cached initial snapshot before stop`);
            this.events.unshift(this.initialFullSnapshot);
          } else {
            // Manually create a full snapshot using rrweb before stopping
            // Use rrweb's record method to create a snapshot
            // This creates a one-time event emission without starting continuous recording
            rrweb.record({
              emit: (event) => {
                if (event.type === 2) { // Full snapshot type
                  console.log(`[Sessionable DEBUG] ✅ Successfully captured final snapshot before stop`);
                  this.events.unshift(event);
                }
              },
              recordCanvas: true,
              inlineStylesheet: true,
              inlineImages: this.options.inlineImages,
            });
          }
        } catch (e) {
          console.error(`[Sessionable] Error capturing final snapshot:`, e);
        }
      }
    }

    // Stop recording
    this.stopFn();
    this.stopFn = null;

    // Clear interval
    if (this.eventsInterval) {
      clearInterval(this.eventsInterval);
      this.eventsInterval = null;
    }

    // Only clear session data if explicitly requested
    if (clearSession) {
      try {
        localStorage.removeItem(Sessionable.SESSION_ID_STORAGE_KEY);
        localStorage.removeItem(Sessionable.SESSION_TIMESTAMP_KEY);
        if (this.options.debug) {
          console.log("[Sessionable] Removed session data from storage");
        }
      } catch (e) {
        console.warn(
          "[Sessionable] Failed to remove session data from localStorage:",
          e
        );
      }
    } else if (this.options.debug) {
      console.log(
        "[Sessionable] Session stopped but data preserved in storage for potential resumption"
      );
    }

    // Check if this is a new session that hasn't sent any batches yet
    // and if it meets the minimum duration requirement
    const sessionMeetsMinDuration =
      !this.isNewSession ||
      this.metrics.batchesSent > 0 ||
      Date.now() - this.sessionStartTime >= this.options.minSessionDuration;

    if (this.options.debug) {
      if (this.isNewSession && this.metrics.batchesSent === 0) {
        const currentDuration = Date.now() - this.sessionStartTime;
        console.log(
          `[Sessionable] Session on stop: duration=${currentDuration}ms, minimum=${this.options.minSessionDuration}ms, ` +
            `will ${sessionMeetsMinDuration ? "send" : "discard"} events`
        );
      }
    }

    // Send any remaining events if we have an ID AND we meet the duration requirements
    if (
      this.anonymousUserId &&
      !this.idGenerationInProgress &&
      sessionMeetsMinDuration
    ) {
      // Process any pending events first
      this.processPendingEvents();

      // Then send any remaining regular events
      if (this.events.length > 0) {
        const finalEvents = [...this.events];
        
        // Final check for full snapshot
        const hasFullSnapshot = finalEvents.some(event => event.type === 2);
        if (!hasFullSnapshot) {
          console.log(`[Sessionable DEBUG] ⚠️ Final batch has no full snapshot`);
        }
        
        this.events = [];
        this.sendEvents(finalEvents);
      }
    } else if (this.options.debug) {
      const reason =
        !this.anonymousUserId || this.idGenerationInProgress
          ? "ID generation is not complete"
          : "session is too short";

      console.log(
        `[Sessionable] Not sending ${
          this.pendingEvents.length + this.events.length
        } events on stop because ${reason}`
      );
    }

    // Update metrics
    this.updateMetrics({
      isRecording: false,
    });
  }

  /**
   * Get current session metrics
   */
  public getMetrics(): SessionMetrics {
    // Make sure we create a proper copy with Date objects intact
    return {
      ...this.metrics,
      // Ensure recordingStartTime remains a Date object
      recordingStartTime: new Date(this.metrics.recordingStartTime),
      lastSuccessfulUpload: this.metrics.lastSuccessfulUpload
        ? new Date(this.metrics.lastSuccessfulUpload)
        : null,
    };
  }

  /**
   * Get the identified user ID, if available
   */
  public getUserId(): string | null {
    return this.userId;
  }

  /**
   * Get the organization ID, if available
   */
  public getOrganizationId(): string | null {
    return this.organizationId;
  }

  /**
   * Get the user attributes, if available
   */
  public getUserAttributes(): Record<string, any> | null {
    return this.userAttributes ? { ...this.userAttributes } : null;
  }

  /**
   * Add custom metadata to the session
   */
  public addMetadata(metadata: Record<string, any>): void {
    this.options.metadata = {
      ...this.options.metadata,
      ...metadata,
    };
  }

  /**
   * Identify the current session with a user ID
   * This links the anonymous session to an identified user
   *
   * @param userId - The ID of the identified user
   * @param organizationId - Optional organization ID to associate with the user
   * @param userAttributes - Optional user metadata/attributes
   * @returns Promise that resolves when identification is complete
   */
  public async identify(
    userId: string,
    organizationId?: string,
    userAttributes?: Record<string, any>
  ): Promise<void> {
    if (!userId) {
      throw new Error("User ID is required for identification");
    }

    if (!this.metrics.sessionId) {
      throw new Error("Cannot identify a session that has not been started");
    }

    // Skip redundant calls with same parameters
    if (
      this.lastIdentifiedUserId === userId &&
      this.lastIdentifiedOrgId === (organizationId || null) &&
      this.userId === userId
    ) {
      if (this.options.debug) {
        console.log(
          `[Sessionable] Skipping redundant identify call for ${userId}`
        );
      }
      return;
    }

    // If identification is already in progress, store this as the pending identify call
    if (this.identifyInProgress) {
      if (this.options.debug) {
        console.log(
          `[Sessionable] Storing pending identify call for ${userId}`
        );
      }

      // Create a promise that will resolve once the current identify call completes and the pending one processes
      return new Promise((resolve, reject) => {
        // Replace any existing pending identify with this newer one
        this.pendingIdentify = async () => {
          try {
            // Call identify again, which will check if parameters changed
            await this.identify(userId, organizationId, userAttributes);
            resolve();
          } catch (error) {
            reject(error);
          }
        };
      });
    }

    this.identifyInProgress = true;

    try {
      // Store the identification info locally
      this.userId = userId;
      this.organizationId = organizationId || null;
      this.userAttributes = userAttributes || {};

      if (this.options.debug) {
        console.log(
          `[Sessionable] Identifying session ${this.metrics.sessionId} with user ${userId}`
        );
      }

      // Update tracking variables immediately
      this.lastIdentifiedUserId = userId;
      this.lastIdentifiedOrgId = organizationId || null;

      // Instead of using the separate /identify endpoint, we'll include the user ID
      // in the next batch of events sent to the /sessions endpoint
      // This avoids issues where the /identify endpoint requires an existing session
      if (this.options.debug) {
        console.log(
          "[Sessionable] User identification set - will be included in next event batch"
        );
      }

      // If we have events waiting to be sent, force a send now to include the identification info
      if (
        this.events.length > 0 &&
        this.anonymousUserId &&
        !this.idGenerationInProgress
      ) {
        const eventsToSend = [...this.events];
        this.events = [];
        await this.sendEvents(eventsToSend);

        if (this.options.debug) {
          console.log(
            "[Sessionable] Sent pending events with user identification"
          );
        }
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      console.error("[Sessionable] User identification failed:", errorMsg);
      this.updateMetrics({
        lastError: `Identification failed: ${errorMsg}`,
      });
      throw error;
    } finally {
      this.identifyInProgress = false;

      // Process pending identify call if one exists
      if (this.pendingIdentify) {
        const pendingCall = this.pendingIdentify;
        this.pendingIdentify = null;

        pendingCall().catch((error) => {
          console.error("[Sessionable] Pending identify call failed:", error);
        });
      }
    }
  }

  /**
   * Send events to the Sessionable API
   */
  private async sendEvents(events: rrweb.eventWithTime[]): Promise<void> {
    if (events.length === 0) return;

    // Check if this is the first batch and ensure it has a snapshot
    if (this.metrics.batchesSent === 0) {
      // Check if the batch contains a full snapshot (event type 2)
      const hasFullSnapshot = events.some(event => event.type === 2);
      
      if (!hasFullSnapshot) {
        console.log(`[Sessionable DEBUG] Waiting for full snapshot before sending first batch`);
        
        // If we have a cached initial snapshot, add it to the beginning of events
        if (this.initialFullSnapshot) {
          console.log(`[Sessionable DEBUG] ✅ Adding cached initial snapshot to first batch`);
          
          if (this.options.debug) {
            console.log(`[Sessionable] Adding initial snapshot to first batch`);
          }
          
          // Add the snapshot to the beginning of events
          events = [this.initialFullSnapshot, ...events];
        } else {
          if (this.options.debug) {
            console.log(`[Sessionable] Delaying first batch until we receive a full snapshot`);
          }
          
          // Return events to the main queue and wait for a full snapshot
          this.events.unshift(...events);
          
          // Try to generate a snapshot
          this.generateFullSnapshot();
          return;
        }
      }
    }
    
    // Ensure we have an ID before sending
    if (!this.anonymousUserId || this.idGenerationInProgress) {
      // Queue these events and return
      this.pendingEvents.push(...events);
      if (this.options.debug) {
        console.log(
          `[Sessionable] Queued ${events.length} events until ID generation completes`
        );
      }
      return;
    }

    // For new sessions, check if we've met the minimum duration requirement
    if (this.isNewSession && this.metrics.batchesSent === 0) {
      const now = Date.now();
      const sessionDuration = now - this.sessionStartTime;

      // If session duration is less than minimum and this is a new session, queue events instead
      if (sessionDuration < this.options.minSessionDuration) {
        if (this.options.debug) {
          console.log(
            `[Sessionable] New session duration (${Math.round(
              sessionDuration
            )}ms) is less than minimum (${
              this.options.minSessionDuration
            }ms). Queueing events.`
          );
        }

        // Add events back to the main queue instead of sending
        this.events.unshift(...events);
        return;
      } else if (this.options.debug) {
        console.log(
          `[Sessionable] Session duration requirement met (${Math.round(
            sessionDuration
          )}ms). Sending first batch.`
        );
      }
    }

    // Update last activity timestamp whenever sending events
    try {
      localStorage.setItem(
        Sessionable.SESSION_TIMESTAMP_KEY,
        Date.now().toString()
      );
    } catch (e) {
      if (this.options.debug) {
        console.warn("[Sessionable] Failed to update session timestamp:", e);
      }
    }

    this.updateMetrics({
      lastBatchSize: events.length,
      batchesSent: this.metrics.batchesSent + 1,
    });

    try {
      // Prepare metadata with basic information
      const metadata = {
        ...this.options.metadata,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        eventCount: events.length,
      };

      // Make sure we have an ID, this should never happen at this point but let's be safe
      if (!this.anonymousUserId) {
        throw new Error("Anonymous ID not available when sending events");
      }

      // Prepare payload according to API format
      const payload: SessionPayload = {
        session_id: this.metrics.sessionId,
        anonymous_id: this.anonymousUserId,
        events: events,
        metadata: metadata,
      };
      
      // Final check: verify we have a snapshot in the payload (essential for debugging)
      const payloadHasSnapshot = payload.events.some(event => event.type === 2);
      if (!payloadHasSnapshot && this.metrics.batchesSent === 0) {
        console.log(`[Sessionable DEBUG] ❌ WARNING: Sending first payload WITHOUT snapshot - playback will likely fail`);
      }

      // Add user identification if available
      if (this.userId) {
        payload.user_id = this.userId;

        if (this.organizationId) {
          payload.organization_id = this.organizationId;
        }

        if (this.userAttributes) {
          payload.user_attributes = this.userAttributes;
        }
      }

      // Determine which endpoint to use
      const apiEndpoint = this.options.debug
        ? this.debugEndpoint
        : this.productionEndpoint;

      if (this.options.debug) {
        console.log(
          "[Sessionable] Debug mode enabled, using endpoint:",
          apiEndpoint
        );
      }

      // Send to API
      await axios.post(apiEndpoint, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.options.apiKey}`,
        },
      });

      // Update metrics on success
      this.updateMetrics({
        connectionStatus: "connected",
        batchesSuccessful: this.metrics.batchesSuccessful + 1,
        lastSuccessfulUpload: new Date(),
        lastError: null,
      });
    } catch (error) {
      // Update metrics on error
      this.updateMetrics({
        connectionStatus: "error",
        lastError: error instanceof Error ? error.message : "Unknown error",
      });
      console.error("[Sessionable] Failed to send session data:", error);
      
      // Special handling for first batch failures to prevent loss of full snapshot
      if (this.metrics.batchesSent === 0) {
        console.log(`[Sessionable DEBUG] ❌ First batch send failed - preserving events for retry`);
        // Return events to the queue for possible retry
        this.events.unshift(...events);
      }
    }
  }

  /**
   * Update session metrics
   */
  private updateMetrics(updates: Partial<SessionMetrics>): void {
    this.metrics = {
      ...this.metrics,
      ...updates,
    };
  }

  /**
   * Initialize the anonymous ID - either from localStorage or by generating a new one
   *
   * This is asynchronous when useDeviceId is true and we need to generate a new ID.
   * When the ID is ready, it will process any pending events.
   */
  private initializeAnonymousId(): void {
    try {
      // First check if we already have an ID in localStorage
      const existingId = localStorage.getItem(Sessionable.ANON_ID_STORAGE_KEY);

      if (existingId) {
        // Use the existing ID
        this.anonymousUserId = existingId;

        if (this.options.debug) {
          console.log("[Sessionable] Using existing anonymous ID:", existingId);
        }

        // Notify with a custom event that ID is ready
        this.notifyIdReady(existingId);
        return;
      }

      // We need to create a new ID
      this.idGenerationInProgress = true;

      if (this.options.useDeviceId) {
        // Use FingerprintJS to generate a device-based ID
        if (this.options.debug) {
          console.log("[Sessionable] Generating device-based ID...");
        }

        this.generateDeviceId()
          .then((deviceBasedId) => {
            if (deviceBasedId) {
              // Save the ID
              this.setAnonymousId(deviceBasedId);

              if (this.options.debug) {
                console.log(
                  "[Sessionable] Device-based ID generated:",
                  deviceBasedId
                );
              }
            } else {
              // Fallback to human-readable ID if generation failed
              this.createHumanReadableId();
            }
          })
          .catch((error) => {
            console.error("[Sessionable] Error generating device ID:", error);
            // Fallback to human-readable ID
            this.createHumanReadableId();
          });
      } else {
        // Generate a human-readable ID immediately
        this.createHumanReadableId();
      }
    } catch (e) {
      console.warn("[Sessionable] Error in ID initialization:", e);
      // Create a fallback ID that's not persisted
      this.createHumanReadableId(false);
    }
  }

  /**
   * Create a human-readable ID
   */
  private createHumanReadableId(persistToStorage: boolean = true): void {
    const randomSuffix = Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, "0");
    const newId = `${humanId({
      separator: "-",
      capitalize: false,
      adjectiveCount: 2,
      addAdverb: true,
    })}-${randomSuffix}`;

    if (this.options.debug) {
      console.log("[Sessionable] Created human-readable ID:", newId);
    }

    // Set the ID and handle any pending events
    this.setAnonymousId(newId, persistToStorage);
  }

  /**
   * Set the anonymous ID and process any pending events
   */
  private setAnonymousId(id: string, persistToStorage: boolean = true): void {
    this.anonymousUserId = id;
    this.idGenerationInProgress = false;

    if (persistToStorage) {
      try {
        localStorage.setItem(Sessionable.ANON_ID_STORAGE_KEY, id);
      } catch (e) {
        console.warn(
          "[Sessionable] Failed to store anonymous ID in localStorage:",
          e
        );
      }
    }

    // Notify that the ID is ready
    this.notifyIdReady(id);

    // Process any pending events
    this.processPendingEvents();
  }

  /**
   * Notify listeners that the ID is ready
   */
  private notifyIdReady(id: string): void {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("sessionable:id_ready", {
          detail: { anonymousId: id },
        })
      );
    }
  }

  /**
   * Process any events that were waiting for the ID to be generated
   */
  private processPendingEvents(): void {
    if (this.pendingEvents.length > 0 && this.anonymousUserId) {
      // Check for full snapshot in pending events (essential for debugging)
      const hasFullSnapshot = this.pendingEvents.some(event => event.type === 2);
      
      if (hasFullSnapshot) {
        console.log('[Sessionable DEBUG] ✅ Found full snapshot in pending events');
        
        // Save the first full snapshot if we haven't already
        if (this.initialFullSnapshot === null) {
          this.initialFullSnapshot = this.pendingEvents.find(event => event.type === 2) || null;
          
          if (this.options.debug && this.initialFullSnapshot) {
            console.log("[Sessionable] Saved initial full snapshot from pending events");
          }
        }
      }
      
      const eventsToProcess = [...this.pendingEvents];
      this.pendingEvents = [];

      if (this.options.debug) {
        console.log(
          `[Sessionable] Processing ${eventsToProcess.length} pending events`
        );
      }

      // Add them to regular events queue
      this.events.push(...eventsToProcess);

      // If we've accumulated enough events, send a batch right away
      if (this.events.length >= this.batchSize) {
        const eventsToSend = [...this.events];
        this.sendEvents(eventsToSend);
        this.events = [];
      }
    }
  }

  /**
   * Add a resumption event to mark page navigation/refresh
   * This creates a custom rrweb event that indicates the session was resumed
   */
  private addResumptionEvent(): void {
    // Create a custom event with type 7 (CustomEvent in rrweb)
    const resumptionEvent = {
      type: 7, // Custom event type
      timestamp: Date.now(),
      data: {
        tag: "sessionable:resume",
        payload: {
          url: window.location.href,
          referrer: document.referrer || "",
          timestamp: Date.now(),
        },
      },
    } as unknown as rrweb.eventWithTime;

    // Add to the beginning of the events array
    this.events.push(resumptionEvent);

    if (this.options.debug) {
      console.log("[Sessionable] Added session resumption event");
    }
  }

  /**
   * Get an existing session ID from localStorage or create a new one
   * Creates a new session if:
   * - No existing session exists
   * - The existing session has expired (30 minutes of inactivity)
   * @returns Object with the session ID and a flag indicating if this is a resumed session
   */
  private getOrCreateSessionId(): { sessionId: string; isResuming: boolean } {
    const storedSessionId = localStorage.getItem(
      Sessionable.SESSION_ID_STORAGE_KEY
    );
    const lastActivityStr = localStorage.getItem(
      Sessionable.SESSION_TIMESTAMP_KEY
    );
    const now = Date.now();

    // Set session start time when getting or creating a session ID
    this.sessionStartTime = now;

    if (this.options.debug) {
      console.log("[Sessionable] getOrCreateSessionId() called:", {
        storedSessionId,
        lastActivityStr,
        now: new Date(now).toISOString(),
      });
    }

    // Check if we have a valid session that hasn't expired
    // IMPORTANT: Only update the timestamp AFTER checking if the session is valid
    if (storedSessionId && lastActivityStr) {
      const lastActivity = parseInt(lastActivityStr, 10);
      const sessionAge = now - lastActivity;

      if (this.options.debug) {
        console.log("[Sessionable] Session age calculation:", {
          lastActivity: new Date(lastActivity).toISOString(),
          sessionAge,
          sessionAgeMinutes: Math.round(sessionAge / 1000 / 60),
          expirationTime: Sessionable.SESSION_EXPIRATION_TIME,
          isExpired: sessionAge >= Sessionable.SESSION_EXPIRATION_TIME,
        });
      }

      // Check if the session has expired (30 minutes of inactivity)
      if (sessionAge < Sessionable.SESSION_EXPIRATION_TIME) {
        // Session is still valid - NOW update the timestamp
        try {
          localStorage.setItem(
            Sessionable.SESSION_TIMESTAMP_KEY,
            now.toString()
          );
        } catch (e) {
          console.warn("[Sessionable] Failed to update session timestamp:", e);
        }

        if (this.options.debug) {
          console.log(
            `[Sessionable] Resuming existing session: ${storedSessionId} (age: ${Math.round(
              sessionAge / 1000 / 60
            )} minutes)`
          );
        }

        // Mark as a resumed session
        this.isNewSession = false;

        return { sessionId: storedSessionId, isResuming: true };
      } else {
        // Session has expired, create a new one
        if (this.options.debug) {
          console.log(
            `[Sessionable] Session expired after ${Math.round(
              sessionAge / 1000 / 60
            )} minutes of inactivity, creating new session`
          );
        }
      }
    } else {
      if (this.options.debug) {
        console.log(
          "[Sessionable] No existing session found, creating new one"
        );
      }
    }

    // Create a new session ID (either no previous session or it expired)
    const newSessionId = uuidv4();

    // Store the new session ID
    try {
      localStorage.setItem(Sessionable.SESSION_ID_STORAGE_KEY, newSessionId);
    } catch (e) {
      console.warn(
        "[Sessionable] Failed to store session ID in localStorage:",
        e
      );
    }

    // Mark as a new session
    this.isNewSession = true;

    if (this.options.debug) {
      console.log("[Sessionable] Starting new session:", newSessionId);
    }
    return { sessionId: newSessionId, isResuming: false };
  }

  /**
   * Generate a device-specific ID using FingerprintJS
   * Maps the fingerprint to a deterministic human-readable ID format
   * Format: "adverb-adjective-adjective-noun-verb-number"
   */
  private async generateDeviceId(): Promise<string | null> {
    try {
      // Initialize FingerprintJS
      const fpPromise = FingerprintJS.load();
      const fp = await fpPromise;

      // Get the visitor identifier
      const result = await fp.get();
      const fingerprint = result.visitorId;

      if (this.options.debug) {
        console.log("[Sessionable] Generated fingerprint:", fingerprint);
      }

      // Create a deterministic ID from the fingerprint
      const deviceId = this.createDeterministicId(fingerprint);

      if (this.options.debug) {
        console.log("[Sessionable] Generated device ID:", deviceId);
      }

      return deviceId;
    } catch (error) {
      console.error("[Sessionable] Error generating device ID:", error);
      return null;
    }
  }

  /**
   * Creates a deterministic human-readable ID from a fingerprint
   * Uses vocabulary from human-id package but with deterministic selection
   */
  /**
   * Uses rrweb's API to manually trigger a full snapshot
   */
  private generateFullSnapshot(): void {
    if (!this.stopFn) return;
    
    if (this.options.debug) {
      console.log("[Sessionable] Attempting to generate a full snapshot");
    }
    
    try {
      // Try to use rrweb's takeFullSnapshot if available
      if (typeof rrweb.takeFullSnapshot === 'function') {
        rrweb.takeFullSnapshot();
        
        if (this.options.debug) {
          console.log("[Sessionable] Manually triggered full snapshot using rrweb.takeFullSnapshot");
        }
      } else {
        // Alternative approach: restart recording briefly to force a new snapshot
        this.restartRecordingForSnapshot();
      }
    } catch (e) {
      console.error("[Sessionable] Failed to generate full snapshot:", e);
      
      // Fallback to restart approach
      this.restartRecordingForSnapshot();
    }
  }
  
  /**
   * Restart recording briefly to force rrweb to generate a new snapshot
   */
  private restartRecordingForSnapshot(): void {
    if (!this.stopFn) return;
    
    const currentStopFn = this.stopFn;
    
    // Save current events
    const savedEvents = [...this.events];
    this.events = [];
    
    // Stop current recording
    currentStopFn();
    
    if (this.options.debug) {
      console.log("[Sessionable] Temporarily stopping recording to force new snapshot");
    }
    
    // Start a new recording to get a fresh snapshot
    const stop = rrweb.record({
      emit: (event) => {
        // Only care about the snapshot event
        if (event.type === 2 && this.initialFullSnapshot === null) {
          this.initialFullSnapshot = event;
          
          if (this.options.debug) {
            console.log("[Sessionable] Generated new full snapshot through restart");
          }
          
          // Restore original events and add the new snapshot
          this.events = [event, ...savedEvents];
          
          // Continue with normal processing
          this.processPendingEvents();
        }
      },
      // Same options as the main recording
      inlineStylesheet: true,
      maskAllInputs: this.options.maskInputs,
      maskTextSelector: this.options.maskTextSelector,
      recordCanvas: true,
      collectFonts: true,
      inlineImages: this.options.inlineImages,
    });
    
    // Update stop function
    this.stopFn = stop;
  }

  private createDeterministicId(fingerprint: string): string {
    // Parse the fingerprint string to select different words deterministically
    // For format: "adverb-adjective-adjective-noun-verb-number"

    // Use chunks of the fingerprint to select different parts of speech
    const adverbIndex =
      parseInt(fingerprint.substring(0, 6), 16) % adverbs.length;
    const adj1Index =
      parseInt(fingerprint.substring(6, 12), 16) % adjectives.length;
    const adj2Index =
      parseInt(fingerprint.substring(12, 18), 16) % adjectives.length;
    const nounIndex =
      parseInt(fingerprint.substring(18, 24), 16) % nouns.length;
    const verbIndex =
      parseInt(fingerprint.substring(24, 30), 16) % verbs.length;

    // Create a numeric suffix from the last part of the fingerprint
    const numericPart = parseInt(fingerprint.substring(30), 16);
    const numericSuffix = (numericPart % 10000).toString().padStart(4, "0");

    // Construct the ID in the format: "adverb-adjective-adjective-noun-verb-number"
    // This matches the "addAdverb: true, adjectiveCount: 2" format of humanId
    const deviceId = `${adverbs[adverbIndex]}-${adjectives[adj1Index]}-${adjectives[adj2Index]}-${nouns[nounIndex]}-${verbs[verbIndex]}-${numericSuffix}`;

    return deviceId;
  }
}
